import React, { useContext, useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useIntl } from 'react-intl';

import useActiveStore from '../../../hooks/useActiveStore';
import Loader from '../../UI/Loader/Loader';
import { Context } from '../Context/StoreLocatorContext';
import formatStoreName from '../../../utils/formatStoreName';
import { magicStrings } from '../utils';
import ActiveStoreDetails from '../ActiveStoreDetails/ActiveStoreDetails';

import {
  Button,
  Content,
  StoreTitle,
  StoreDetails,
  Popup,
  PopupCloseButton,
  CloseIcon,
} from './styles';

const siteHeaderQuery = graphql`
  query ActiveStoreLocationQuery {
    siteHeaderEN: contentfulHeader(
      contentful_id: { eq: "32WWiqIbDJlMo8yi7SJ000" }
      node_locale: { eq: "en-CA" }
    ) {
      storeLocatorBubble
    }
    siteHeaderFR: contentfulHeader(
      contentful_id: { eq: "32WWiqIbDJlMo8yi7SJ000" }
      node_locale: { eq: "fr-CA" }
    ) {
      storeLocatorBubble
    }
  }
`;

interface Props {
  className?: string;
  onClick?: () => void;
  role?: string;
}
// eslint-disable-next-line complexity
const ActiveStoreLocation = ({ className, onClick, role }: Props) => {
  const intl = useIntl();
  const data = useContext(Context);
  const { activeStore } = useActiveStore();
  const { siteHeaderEN, siteHeaderFR } = useStaticQuery(siteHeaderQuery);
  const siteHeader = intl.locale === 'fr' ? siteHeaderFR : siteHeaderEN;
  const {
    bladeState,
    nearbyStores,
    setBladeState,
    setStoreToDisplay,
    setShowSelectBtn,
    setHideBookAppointment,
  } = data;

  const [loading, setLoading] = useState(
    !(nearbyStores?.length),
  );
  const [displayName, setDisplayName] = useState(
    formatStoreName(activeStore) || '',
  );
  const [showPopup, setShowPopup] = useState<boolean | null>(null);
  const whenClicked = () => {
    if (onClick) onClick();
    setStoreToDisplay(activeStore);
    setHideBookAppointment(false);
    setShowSelectBtn(false);
    setBladeState(!bladeState);
  };

  const closePopup = (e) => {
    if (e) e.stopPropagation();
    setShowPopup(false);
  };

  useEffect(() => {
    if (activeStore) {
      setDisplayName(formatStoreName(activeStore));
    }
  }, [activeStore]);

  useEffect(() => {
    setLoading(!(nearbyStores?.length));
  }, [nearbyStores]);

  useEffect(() => {
    setTimeout(() => {
      if (!activeStore && !bladeState && showPopup === null) {
        setShowPopup(true);
      }
    }, 6000);
  }, [bladeState, showPopup, activeStore]);

  return (
    <Button
      className={className}
      onClick={whenClicked}
      aria-expanded={!showPopup}
      aria-label={siteHeader.storeLocatorBubble}
      role={role}
      activeStore={activeStore}
      data-testid="active-store-location-button"
    >
      {!activeStore && siteHeader?.storeLocatorBubble && (
        <Popup className={`${showPopup ? 'visible' : 'hidden'} ${className}`}>
          {siteHeader.storeLocatorBubble}
          <PopupCloseButton
            tabIndex={0}
            onClick={closePopup}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowPopup(false);
              }
            }}
          >
            {magicStrings.close(intl.locale)}
            <CloseIcon />
          </PopupCloseButton>
        </Popup>
      )}
      <Content>
        <strong className="icon">
          <StoreTitle>
            {displayName || magicStrings.myStore(intl.locale)}
          </StoreTitle>
        </strong>
        <br />
        {!activeStore && loading && <Loader className="small" />}
        {!activeStore && !loading && (
          <StoreDetails>{magicStrings.selectStore(intl.locale)}</StoreDetails>
        )}
        {activeStore && <ActiveStoreDetails />}
      </Content>
    </Button>
  );
};

export default ActiveStoreLocation;
